// client/src/components/pages/LexiStar/Declarations/SegmentDrawMap.jsx
//
// Draw a road segment for a declaration's scope, and optionally its alternate
// corridor, on the shared LexiStarMap (LS-09 §5.21 "corridor segment drawn on
// the map"). Tap/click adds a point to the line being drawn; Undo and Clear
// edit it. The corridor band is drawn exactly `corridorM` metres wide at every
// zoom (the Routes editor's line-width-in-metres technique), so what you see
// is what the engine will test against. A reference route can be shown to
// trace along. Desktop/tablet tool (LS-09 §9: editing is not phone scope), but
// it stays usable by touch.

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Source, Layer, Marker } from 'react-map-gl/maplibre';
import LexiStarMap from '../../../map/LexiStarMap';
import { corridorLineWidth, meanLat, bboxOf, lineKm } from '../Routes/routeGeometry';
import { fmtNum } from '../StatusBoard/format';

const SEGMENT = '#0f172a';
const ALT = '#475569';
const REFERENCE = '#2563eb';
const DEFAULT_VIEW = { longitude: 28.5, latitude: -26.2, zoom: 7 };

const lineFC = (coords, props = {}) => ({
  type: 'FeatureCollection',
  features:
    Array.isArray(coords) && coords.length >= 2
      ? [
          {
            type: 'Feature',
            properties: props,
            geometry: { type: 'LineString', coordinates: coords },
          },
        ]
      : [],
});

const round = (n) => Math.round(n * 1e6) / 1e6;

export default function SegmentDrawMap({
  segment = [],
  onSegmentChange,
  corridorM = 500,
  altLine = [],
  onAltChange,
  altCorridorM = 500,
  drawing = 'segment', // 'segment' | 'alt' | null
  referenceLines = [],
  className = 'relative z-0 h-[360px] lg:h-[440px] rounded-xl overflow-hidden border border-slate-200',
}) {
  const mapRef = useRef(null);
  const active = drawing === 'alt' ? altLine : segment;
  const setActive = drawing === 'alt' ? onAltChange : onSegmentChange;

  const lat = useMemo(
    () => meanLat(segment.length ? segment : altLine) || DEFAULT_VIEW.latitude,
    [segment, altLine]
  );
  const segWidth = useMemo(() => corridorLineWidth(Number(corridorM) || 0, lat), [corridorM, lat]);
  const altWidth = useMemo(
    () => corridorLineWidth(Number(altCorridorM) || 0, lat),
    [altCorridorM, lat]
  );
  const refFC = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: referenceLines.map((r) => r.feature).filter(Boolean),
    }),
    [referenceLines]
  );

  // Frame the reference route when one is chosen and nothing is drawn yet.
  const refKey = referenceLines.map((r) => r.key).join('|');
  const fitTo = useCallback((pts) => {
    const b = bboxOf(pts);
    if (b && mapRef.current) {
      try {
        mapRef.current.fitBounds(b, { padding: 48, duration: 300, maxZoom: 14 });
      } catch {
        /* not ready */
      }
    }
  }, []);
  useEffect(() => {
    if (segment.length || !refKey) return;
    fitTo(refFC.features.flatMap((f) => f.geometry?.coordinates || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refKey]);

  const onClick = (e) => {
    if (!drawing || !setActive || !e?.lngLat) return;
    setActive([...active, [round(e.lngLat.lng), round(e.lngLat.lat)]]);
  };

  return (
    <div>
      <div className={className}>
        <LexiStarMap
          className="absolute inset-0"
          initialViewState={DEFAULT_VIEW}
          defaultBasemap="street"
          navigationPosition="top-left"
          cursor={drawing ? 'crosshair' : undefined}
          onClick={onClick}
          onLoad={(e) => {
            mapRef.current = e.target;
            if (segment.length) fitTo([...segment, ...altLine]);
          }}
          overlay={
            drawing ? (
              <div className="absolute top-3 right-3 z-[1000] flex gap-1.5">
                <button
                  type="button"
                  onClick={() => setActive(active.slice(0, -1))}
                  disabled={!active.length}
                  className="bg-surface/95 backdrop-blur rounded-lg shadow-lg px-3 py-1.5 text-xs font-medium text-slate-700 disabled:opacity-50"
                >
                  Undo point
                </button>
                <button
                  type="button"
                  onClick={() => setActive([])}
                  disabled={!active.length}
                  className="bg-surface/95 backdrop-blur rounded-lg shadow-lg px-3 py-1.5 text-xs font-medium text-slate-700 disabled:opacity-50"
                >
                  Clear
                </button>
              </div>
            ) : null
          }
        >
          {refFC.features.length > 0 && (
            <Source id="decl-reference" type="geojson" data={refFC}>
              <Layer
                id="decl-reference-line"
                type="line"
                paint={{ 'line-color': REFERENCE, 'line-width': 3, 'line-opacity': 0.7 }}
              />
            </Source>
          )}
          {altLine.length >= 2 && (
            <Source id="decl-alt" type="geojson" data={lineFC(altLine)}>
              <Layer
                id="decl-alt-band"
                type="line"
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{ 'line-color': ALT, 'line-width': altWidth, 'line-opacity': 0.14 }}
              />
              <Layer
                id="decl-alt-line"
                type="line"
                paint={{ 'line-color': ALT, 'line-width': 2.5, 'line-dasharray': [2, 1.5] }}
              />
            </Source>
          )}
          {segment.length >= 2 && (
            <Source id="decl-segment" type="geojson" data={lineFC(segment)}>
              <Layer
                id="decl-segment-band"
                type="line"
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{ 'line-color': SEGMENT, 'line-width': segWidth, 'line-opacity': 0.16 }}
              />
              <Layer
                id="decl-segment-line"
                type="line"
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{ 'line-color': SEGMENT, 'line-width': 3 }}
              />
            </Source>
          )}
          {active.map(([lon, la], i) => (
            <Marker key={`${drawing}-${i}`} longitude={lon} latitude={la}>
              <div
                className="w-3 h-3 rounded-full shadow"
                style={{ background: '#ffffff', border: `2px solid ${SEGMENT}` }}
                title={`Point ${i + 1}`}
              />
            </Marker>
          ))}
        </LexiStarMap>
      </div>
      <p className="mt-2 text-xs text-slate-500 tabular-nums" aria-live="polite">
        {drawing === 'alt' ? 'Alternate corridor' : 'Segment'}: {active.length} point
        {active.length === 1 ? '' : 's'}
        {active.length >= 2 && ` · ${fmtNum(lineKm(active), 1)} km`}
        {drawing ? ' — click the map to add points along the road.' : ''}
      </p>
      {!drawing && segment.length === 0 && (
        <p className="text-xs text-slate-500">Choose “Road segment” as the scope to draw.</p>
      )}
    </div>
  );
}
